import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, IconSpinner } from '../../../components';
import { getProviderEventDashboardTransactions } from '../../../util/api';
import classNames from 'classnames';
import css from '../EventProviderDashboardPageForm.module.css';

const PER_PAGE = 10;

const getInvoiceType = tx => {
  const pd = tx?.attributes?.protectedData || {};
  if (pd?.invoiceData?.invoiceType) return pd.invoiceData.invoiceType;
  if (pd?.isOvertimeInvoice) return 'overtime';
  if (pd?.isExpenseInvoice) return 'expense';
  if (pd?.isReductionInvoice) return 'reduction';
  return 'invoice';
};

const getInvoiceLabel = type => {
  switch (type) {
    case 'overtime': return 'Overtime';
    case 'expense': return 'Expense';
    case 'reduction': return 'Reduction';
    default: return 'Invoice';
  }
};

const getStateLabel = state => {
  switch (state) {
    case 'state/offer-pending': return 'Pending';
    case 'state/customer-offer-pending': return 'Counter Sent';
    case 'state/offer-accepted': return 'Accepted';
    case 'state/reduction-accepted': return 'Credit Agreed';
    case 'state/offer-rejected': return 'Rejected';
    case 'state/pending-payment': return 'Payment Pending';
    case 'state/payment-expired': return 'Expired';
    case 'state/canceled': return 'Cancelled';
    case 'state/delivered': return 'Delivered';
    case 'state/completed': return 'Completed';
    case 'state/reviewed':
    case 'state/reviewed-by-customer':
    case 'state/reviewed-by-provider': return 'Reviewed';
    default: return 'Pending';
  }
};

const getStateCss = state => {
  switch (state) {
    case 'state/offer-accepted':
    case 'state/delivered':
    case 'state/completed':
    case 'state/reviewed':
    case 'state/reviewed-by-customer':
    case 'state/reviewed-by-provider': return css.statusBadgeCompleted;
    case 'state/reduction-accepted': return css.statusBadgeCompleted;
    case 'state/offer-rejected':
    case 'state/canceled':
    case 'state/payment-expired': return css.statusBadgeCancelled;
    default: return css.statusBadgePending;
  }
};

const ProviderInvoicesTab = ({ currentUserId }) => {
  const history = useHistory();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProviderEventDashboardTransactions({ userId: currentUserId, page: currentPage, perPage: PER_PAGE })
      .then(res => {
        if (cancelled) return;
        setInvoices(res?.data?.transactions || []);
        const meta = res?.data?.meta || {};
        setTotalPages(meta.totalPages || 1);
      })
      .catch(() => { if (!cancelled) setError('Failed to fetch invoices'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentUserId, currentPage]);

  const handleView = txId => history.push(`/order/${txId}`);

  if (loading) {
    return (
      <div className={css.eventDashboardFormContainer}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <IconSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.eventDashboardFormContainer}>
        <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className={css.eventDashboardFormContainer}>
      <div className={css.eventDashboardFormHeader}>
        <div className={css.eventDashboardFormTitleContainer}>
          <h1 className={css.eventDashboardFormTitle}>Invoices</h1>
        </div>
      </div>

      <div className={css.cardContainer}>
        <div className={css.eventDashboardTableContainer}>
          <table className={css.eventDashboardTable}>
            <thead>
              <tr>
                <th>EVENT</th>
                <th>TYPE</th>
                <th>AMOUNT</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                    No invoices found
                  </td>
                </tr>
              ) : (
                invoices.map((item, index) => {
                  const tx = item?.transaction;
                  const state = tx?.attributes?.state;
                  const pd = item?.protectedData || {};
                  const invoiceType = getInvoiceType(tx);
                  const amount = pd?.invoiceData?.totalAmount || pd?.totalAmount;
                  const amountDisplay = amount ? `$${(amount / 100).toFixed(2)}` : '—';
                  const title = item?.listing?.attributes?.title || '—';

                  return (
                    <tr key={`${item.transactionId}-${index}`}>
                      <td>
                        <div className={css.eventDetails}>
                          <div className={css.eventTitle}>{title}</div>
                          <div className={css.eventId}>{item.transactionId}</div>
                        </div>
                      </td>
                      <td>{getInvoiceLabel(invoiceType)}</td>
                      <td>{amountDisplay}</td>
                      <td>
                        <span className={classNames(css.statusBadge, getStateCss(state))}>
                          {getStateLabel(state)}
                        </span>
                      </td>
                      <td>
                        <Button
                          onClick={() => handleView(item.transactionId)}
                          className={classNames(css.actionButton, css.actionButtonView)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={css.paginationContainer}>
            <button
              className={css.paginationArrow}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="#383F52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <div className={css.paginationNumbers}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <span
                  key={p}
                  className={classNames(css.paginationNumber, { [css.paginationNumberActive]: p === currentPage })}
                  onClick={() => setCurrentPage(p)}
                  style={{ cursor: 'pointer' }}
                >{p}</span>
              ))}
            </div>
            <button
              className={css.paginationArrow}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="#383F52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderInvoicesTab;
